import { useEffect, useMemo, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { FiCopy } from "react-icons/fi";
import { useTranslation } from "react-i18next";

import { getMyAccountData } from "../api/api";
import InfoLayout from "../components/info/InfoLayout";
import { normalizeStatus, getStatusLabel } from "../utils/orderStatus";

import "../styles/OrderDetailsPage.css";

export default function OrderDetailsPage({ chatId = "" }) {
  const { t, i18n } = useTranslation("orders");
  const { orderId } = useParams();
  const location = useLocation();

  const fromChatId = location.state?.fromChatId || chatId || "";

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const decodedId = useMemo(() => {
    try {
      return decodeURIComponent(orderId || "");
    } catch {
      return orderId || "";
    }
  }, [orderId]);

  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      try {
        const data = await getMyAccountData();
        const all = Array.isArray(data?.purchases) ? data.purchases : [];

        const paidOnly = all.filter((p) => normalizeStatus(p.status) === "paid");

        const found =
          paidOnly.find((p) => String(p.id) === String(decodedId)) ||
          paidOnly.find((p) => String(p.receipt_number) === String(decodedId)) ||
          null;

        if (alive) setOrder(found);
      } catch (e) {
        console.error("OrderDetailsPage load error:", e);
        if (alive) setOrder(null);
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [decodedId]);

  async function copy(text) {
    try {
      await navigator.clipboard.writeText(String(text || ""));
    } catch (e) {
      console.error("copy failed", e);
    }
  }

  function formatMoney(value) {
    const n = Number(value || 0);
    const locale = i18n.language?.startsWith("uk") ? "uk-UA" : "en-US";

    return `${new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(n)} ${t("currency_symbol")}`;
  }

  if (loading) {
    return (
      <InfoLayout chatId={fromChatId} title={t("history_title")}>
        <div className="odWrap">
          <div className="odHeaderCard odSkeletonCard">
            <div className="odTop">
              <div className="odSkeleton odSkeletonBadge" />
              <div className="odSkeleton odSkeletonTotal" />
            </div>

            <div className="odSkeleton odSkeletonMeta" />

            <div className="odImgs">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="odImgWrap odSkeleton odSkeletonImg" />
              ))}
            </div>
          </div>

          <div className="odItemsTitle"></div>

          <div className="odItems">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="odItemCard">
                <div className="odItemImg odSkeleton odSkeletonItemImg" />

                <div className="odItemInfo">
                  <div className="odSkeleton odSkeletonLine odSkeletonLineLg" />
                  <div className="odSkeleton odSkeletonLine odSkeletonLineMd" />
                  <div className="odSkeleton odSkeletonLine odSkeletonLineSm" />
                </div>

                <div className="odSkeleton odSkeletonPrice" />
              </div>
            ))}
          </div>
        </div>
      </InfoLayout>
    );
  }

  if (!order) {
    return (
      <InfoLayout chatId={fromChatId} title={t("history_title")}>
        <div className="odWrap">
          <div className="odEmpty">{t("not_found")}</div>
        </div>
      </InfoLayout>
    );
  }

  const status = normalizeStatus(order.status);
  const items = Array.isArray(order.items) ? order.items : [];
  const tracking =
    order.tracking_number || order.tracking || order.trackingNumber || "";

  return (
    <InfoLayout chatId={fromChatId} title={t("history_title")}>
      <div className="odWrap">
        <div className="odHeaderCard">
          <div className="odTop">
            <div className={`odStatus st-${status}`}>
              {getStatusLabel(order.status)}
            </div>

            <div className="odTotal">
              {typeof order.total === "number" ? formatMoney(order.total) : order.total}
            </div>
          </div>

          <div className="odMeta">
            {t("order_number")}
            {order.receipt_number} &nbsp; {order.date || ""}
          </div>

          {!!items.length && (
            <div className="odImgs">
              {items.slice(0, 8).map((it, idx) => (
                <div key={it.id ?? `${order.id}-${idx}`} className="odImgWrap">
                  <img
                    src={it.image_url}
                    alt={it.title || t("fallback_item_lower")}
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          )}

          {!!tracking && (
            <div className="odTrackRow">
              <div className="odTrackLabel">{t("tracking_number")}</div>
              <div className="odTrackValue">{tracking}</div>
              <button
                type="button"
                className="odCopyBtn"
                onClick={() => copy(tracking)}
                aria-label={t("copy_tracking")}
                title={t("copy")}
              >
                <FiCopy />
              </button>
            </div>
          )}
        </div>

        <div className="odItemsTitle">
          {items.length} {t("items_count_suffix")}
        </div>

        <div className="odItems">
          {items.map((it, idx) => (
            <div key={it.id ?? `${order.id}-it-${idx}`} className="odItemCard">
              <div className="odItemImg">
                <img
                  src={it.image_url}
                  alt={it.title || t("fallback_item_lower")}
                  loading="lazy"
                />
              </div>

              <div className="odItemInfo">
                <div className="odItemName">
                  {it.title || it.name || t("fallback_item")}
                </div>

                {!!it.color && (
                  <div className="odItemSub">
                    {t("color")}: {it.color}
                  </div>
                )}

                {!!it.size && (
                  <div className="odItemSub">
                    {t("size")}: {it.size}
                  </div>
                )}

                {!!it.qty && (
                  <div className="odItemSub">
                    {t("quantity")}: {it.qty}
                  </div>
                )}

                {!it.qty && it.quantity != null && (
                  <div className="odItemSub">
                    {t("quantity")}: {it.quantity}
                  </div>
                )}
              </div>

              <div className="odItemPrice">
                {typeof it.price === "number" ? formatMoney(it.price) : it.price}
              </div>
            </div>
          ))}
        </div>
      </div>
    </InfoLayout>
  );
}